import { Dialog } from '@angular/cdk/dialog';
import { APP_BASE_HREF } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  inject,
  LOCALE_ID,
  NgZone
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MetaDefinition } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet
} from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import Hotjar from '@hotjar/browser';
import { NgProgressbar } from 'ngx-progressbar';
import { NgProgressRouter } from 'ngx-progressbar/router';
import {
  filter,
  fromEvent,
  map,
  Observable,
  startWith,
  switchMap
} from 'rxjs';

import { JsonLdService } from '@core/common/json-ld.service';
import { MediaQueryService } from '@core/breakpoints';
import { BrowserApiService, RouteData } from '@core/common';
import { WINDOW } from '@core/common/tokens';
import { setRootCssProperty } from '@core/helpers';
import { ServerTimingService } from '@core/http/server-timing.service';
import { AlternateService, CanonicalService, MetaService } from '@core/meta';
import { Notifier } from '@core/notifications';
import { SettingsState } from '@core/settings';
import { WebsocketClient } from '@core/websocket';
import { environment, SupportedLocale } from '@env';
import { registerDayjsLocale } from '@env/locales';
import { LandingMetaService } from '@core/meta/landing-meta.service';

import { AuthState } from './modules/users';
import { AffiliateService } from './modules/users/affiliate.service';
import { MarkerService } from './modules/users/data-collector/marker.service';
import { TelegramBotService } from './modules/users/telegram-bot.service';
import { WrapperState } from './shell';

const APP_UPDATED_NOTIFICATION = $localize`:@@system.update_installed:Сайт был обновлен, обновите страницу!`;

@Component({
  selector: 'app-root',
  template: ` <ng-progress ngProgressRouter aria-label="loading" />

    <div [@.disabled]="(settingsState.disableAnimations() || isLg()) && !router.url.startsWith('/event')">
      <router-outlet />
    </div>`,
  imports: [
    NgProgressbar,
    NgProgressRouter,
    RouterOutlet
  ]
})
export class AppComponent implements AfterViewInit {
  public readonly settingsState = inject(SettingsState);
  public readonly router = inject(Router);

  private readonly currentLocale = inject<SupportedLocale>(LOCALE_ID);
  private readonly window = inject(WINDOW);
  private readonly baseHref = inject(APP_BASE_HREF, { optional: true });
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly browser = inject(BrowserApiService);
  private readonly notifier = inject(Notifier);
  private readonly markerService = inject(MarkerService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dialog = inject(Dialog);
  private readonly meta = inject(MetaService);
  private readonly alternateService = inject(AlternateService);
  private readonly canonicalService = inject(CanonicalService);
  private readonly landingMetaService = inject(LandingMetaService);
  private readonly ngZone = inject(NgZone);
  private readonly authState = inject(AuthState);
  private readonly affiliateService = inject(AffiliateService);
  private readonly wrapperState = inject(WrapperState);
  private readonly websocketClient = inject(WebsocketClient);
  private readonly serverTiming = inject(ServerTimingService);
  private readonly telegramBotService = inject(TelegramBotService);
  private readonly mediaService = inject(MediaQueryService);
  private readonly jsonLdService = inject(JsonLdService);

  public isLg = toSignal(this.mediaService.mediaQuery('max', 'lg'));

  private isHotjarIdentified = false;
  private readonly websocketAuthEffect = effect(() => {
    const token = this.authState.token() || this.markerService.marker;
    const isConnected = this.websocketClient.isConnected();

    if (isConnected) {
      const message = {
        event: 'auth',
        data: null,
        token
      };

      this.websocketClient.send(message, true);
    }

    if (Hotjar.isReady() && !this.isHotjarIdentified) {
      Hotjar.identify(this.authState.user()?.id?.toString() || null, {
        nickname: this.authState.user()?.nickname || ''
      });
      this.isHotjarIdentified = true;
    }
  });

  constructor() {
    this.serverTiming.start('app-component', 'Render app component');

    registerDayjsLocale(this.currentLocale);

    this.listenRouterEvents();
    this.setWrapperClassList();

    if (this.browser.isBrowser()) {
      this.connectToWebsocket();
      this.listenResizeEvent();
      this.ensureAppIsUpdated();
      this.optimizeImageRendering();
      this.affiliateSubscribe();
      this.telegramBot();
    }

    Hotjar.init(environment.hotjarSiteId, 6);
  }

  ngAfterViewInit(): void {
    this.serverTiming.end('app-component');
  }

  private listenRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route = this.activatedRoute;

          while (route.firstChild) {
            route = route.firstChild;
          }

          return route;
        }),
        filter((route: ActivatedRoute) => route.outlet === 'primary'),
        switchMap((route: ActivatedRoute) => route.data as Observable<RouteData>),
        map((data: RouteData) => {
          return {
            url: this.router.url,
            data
          };
        }),
        startWith({
          url: this.window?.location.pathname || this.baseHref!,
          data: {}
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((routeData) => {
        this.dialog.closeAll();
        this.canonicalService.setCanonicalURL((this.window?.location?.href || '').split('?')[0]);
        this.alternateService.setAlternateURL(this.window?.location?.pathname || '');
        this.landingMetaService.setTwitterUrl((this.window?.location?.href || '').split('?')[0]);
        this.landingMetaService.setBase((this.window?.location?.href || '').split('?')[0]);
        this.setMetaTags(routeData.data);
        this.wrapperState.setPath(routeData.url);
        this.loadJsonLd(routeData.url);
      });
  }

  private loadJsonLd(url: string): void {
    const localeForJsonLd = this.currentLocale === 'ru' ? 'ru' : 'en';
    const sanitizedRoute = url.replace(/^\/(ru|en|pt-BR|tr|uk|pl|cs|fr|de|pt|ro|es|sv)\//, '').replace(/^\/+/, '');

    this.jsonLdService.loadJsonLd(sanitizedRoute, localeForJsonLd);
  }

  private setMetaTags(data: RouteData): void {
    if ('skip' === data.meta) {
      return;
    }

    this.meta.setDefaults(this.router.url);

    if (data.meta) {
      const meta = {
        ...data.meta
      };

      if (meta.title) {
        this.meta.setTitle(meta.title, !!meta.rawTitle);
      }

      delete meta.rawTitle;

      this.meta.setTags(meta as MetaDefinition);
    } else if (!this.meta.hasTitle) {
      this.meta.setDefaultTitle();
    }
  }

  private connectToWebsocket(): void {
    this.websocketClient.connect();
  }

  private affiliateSubscribe(): void {
    this.affiliateService.checkQueryParams().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private telegramBot(): void {
    this.telegramBotService.init();
  }

  private listenResizeEvent(): void {
    if (!this.isLg()) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.setVhVariable();

      fromEvent(this.window!, 'resize')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.setVhVariable());
    });
  }

  private setVhVariable(): void {
    setRootCssProperty('vh', `${this.window!.innerHeight}px`);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private async ensureAppIsUpdated() {
    if (!this.swUpdate || !this.swUpdate.isEnabled) {
      return;
    }

    const hasNewVersion = await this.swUpdate.checkForUpdate();

    if (hasNewVersion) {
      await this.swUpdate.activateUpdate();

      this.notifier.success(APP_UPDATED_NOTIFICATION, { autoClose: false });
    }
  }

  private optimizeImageRendering(): void {
    if ((this.window as any).chrome) {
      setRootCssProperty('image-rendering', '-webkit-optimize-contrast');
    }
  }

  private setWrapperClassList(): void {
    if (this.settingsState.disableAnimations()) {
      this.wrapperState.addClass('animations_off');
    }

    if (this.settingsState.streamerMode()) {
      this.wrapperState.addClass('streamer_mode');
    }
  }
}
